/**
 * The position identity bar (CLAUDE.md 5.7, 2.2).
 *
 * Everything that determines the numbers, always on screen: module and version,
 * design code and parameter set, review state, and whether the stored result is stale.
 *
 * This exists because reproducibility that a user cannot *see* provides no confidence.
 * Section 5.7 pins four identifiers and calls the guarantee "the most important
 * non-obvious requirement in the document"; without this bar the guarantee is real but
 * invisible, and an engineer has no way to tell a current result from one produced by
 * a module version that has since moved on.
 */
import { AlertTriangle, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Position } from '@/api/types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import { formatRelative } from '@/lib/format'

const REVIEW_KEYS: Record<Position['review_state'], string> = {
  none: 'review.state.none',
  submitted: 'review.state.submitted',
  checked: 'review.state.checked',
  approved: 'review.state.approved',
}

export function PositionIdentity({
  position,
  projectName,
  dirty,
  onSave,
  saving,
}: {
  position: Position
  projectName?: string
  dirty: boolean
  onSave: () => void
  saving: boolean
}) {
  const { t, i18n } = useTranslation()

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {projectName && <span className="text-sm text-text-muted">{projectName}</span>}

        {/* The pinned quadruple. Module id, version, code and parameter set are
            identifiers, never translated (14.4). */}
        <span className="tabular text-xs text-text-muted">
          {t('position.pinned', {
            module: position.module_id,
            version: position.module_version,
            code: position.code_id,
            paramSet: position.param_set_version,
          })}
        </span>

        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-xs',
            position.review_state === 'approved'
              ? 'bg-[var(--status-pass-bg)] text-[var(--status-pass)]'
              : 'bg-surface-raised text-text-muted',
          )}
        >
          {t(REVIEW_KEYS[position.review_state])}
        </span>

        {position.calculated_at && (
          <span className="text-xs text-text-muted">
            {t('common.updated')} {formatRelative(position.calculated_at, i18n.language)}
          </span>
        )}

        <div className="ms-auto flex items-center gap-2">
          {dirty && (
            <span className="text-xs text-[var(--status-warn)]">{t('common.unsaved')}</span>
          )}
          <Button size="sm" variant="primary" onClick={onSave} loading={saving} disabled={!dirty}>
            <Save size={15} aria-hidden />
            {t('action.save')}
          </Button>
        </div>
      </div>

      {/*
        The saved result no longer matches the saved inputs -- computed server-side
        from the fingerprint, never a flag that a write path could forget (5.7).
        Deliberately a full-width banner rather than a subtle badge: a stale number
        read as a current one is the worst failure this interface can have.
      */}
      {position.stale && (
        <p className="flex items-center gap-2 rounded-md bg-[var(--status-warn-bg)] px-3 py-2 text-sm text-[var(--status-warn)]">
          <AlertTriangle size={15} className="shrink-0" aria-hidden />
          {t('position.stale_banner')}
        </p>
      )}
    </div>
  )
}
